import React, { useState, useRef, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import CssBaseline from "@material-ui/core/CssBaseline";
import Hidden from "@material-ui/core/Hidden";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { useTheme } from "@material-ui/core/styles";
import AdminHeader from "../components/AdminHeader";
import Navigator from "../components/Navigator";
import AccountantBanner from "../components/AccountantBanner";

import { drawerWidth, drawerWidthCollapsed, Theme } from "../theme";
import { connect } from "react-redux";
import { AppState } from "../store";
import { Dispatch } from "redux";
import { toggleMobileOpen } from "../store/adminHtml/actions";
import { AppConfig } from "../config";
import { useRecordEvent } from "src/hooks/useRecordEvent";

const useStyles = makeStyles((theme: Theme) => {
  const { custom: palette } = theme.palette.custom;

  return {
    root: {
      display: "flex",
      minHeight: "100vh",
      backgroundColor: palette.bg,
    },
    drawer: {
      [theme.breakpoints.up("sm")]: {
        width: drawerWidth,
        flexShrink: 0,
      },
    },
    appContent: {
      flex: 1,
      display: "flex",
      flexDirection: "column",
      backgroundColor: palette.bg,
    },
  };
});

interface AdminHtmlOwnProps {
  pageTitle: string;
  pageDescription: string;
  walkthroughId?: string;
  children: React.ReactNode;
}

interface AdminHtmlStateProps {
  mobileOpen: boolean;
}

interface AdminHtmlDispatchProps {
  onDrawerToggle: () => void;
}

type AdminHtmlProps = AdminHtmlOwnProps &
  AdminHtmlStateProps &
  AdminHtmlDispatchProps;

const AdminHtml = (props: AdminHtmlProps) => {
  const recordEvent = useRecordEvent();

  const classes = useStyles();
  const muiTheme = useTheme();
  const isDesktop = useMediaQuery(muiTheme.breakpoints.up("sm"));

  const [dialog, setDialog] = useState<"settings" | null>(null);
  const [collapsed, setCollapsed] = useState(() => {
    const saved = localStorage.getItem("nav-collapsed");
    return saved === "true";
  });

  const navRef = useRef<HTMLElement>(null);

  const handleToggleCollapse = () => {
    setCollapsed((prev) => {
      const newValue = !prev;
      localStorage.setItem("nav-collapsed", String(newValue));
      recordEvent({
        type: "feature-adoption",
        feature: "drawer-toggle",
        state: newValue ? "closed" : "open",
      });
      return newValue;
    });
  };

  // Trigger window resize when drawer transition completes so charts resize
  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;

    const handleTransitionEnd = (e: TransitionEvent) => {
      if (e.propertyName === "width") {
        window.dispatchEvent(new Event("resize"));
      }
    };

    nav.addEventListener("transitionend", handleTransitionEnd);
    return () => nav.removeEventListener("transitionend", handleTransitionEnd);
  }, []);

  // Trigger window resize when mobile drawer opens/closes
  useEffect(() => {
    window.dispatchEvent(new Event("resize"));
  }, [props.mobileOpen]);

  const currentDrawerWidth = collapsed ? drawerWidthCollapsed : drawerWidth;

  return (
    <div className={classes.root}>
      <CssBaseline />
      <nav
        ref={navRef}
        className={classes.drawer}
        style={
          isDesktop
            ? { width: currentDrawerWidth, transition: "width 0.3s ease" }
            : undefined
        }
      >
        <Hidden smUp implementation="js">
          <Navigator
            PaperProps={{ style: { width: drawerWidth } }}
            variant="temporary"
            open={props.mobileOpen}
            onClose={props.onDrawerToggle}
            title={AppConfig.title}
            subTitle="Dashboard"
            onClickSubscriptionDetails={() => {
              setDialog("settings");
            }}
          />
        </Hidden>
        <Hidden xsDown implementation="css">
          <Navigator
            PaperProps={{
              style: {
                width: currentDrawerWidth,
                transition: "width 0.3s ease",
                overflow: "visible",
              },
            }}
            open={props.mobileOpen}
            variant="permanent"
            title={AppConfig.title}
            subTitle="Dashboard"
            collapsed={collapsed}
            onToggleCollapse={handleToggleCollapse}
            onClickSubscriptionDetails={() => {
              setDialog("settings");
            }}
          />
        </Hidden>
      </nav>
      <div
        className={classes.appContent}
        style={
          isDesktop
            ? {
                width: `calc(100% - ${currentDrawerWidth}px)`,
                transition: "width 0.3s ease",
              }
            : undefined
        }
      >
        <AccountantBanner />
        <AdminHeader
          onDrawerToggle={props.onDrawerToggle}
          title={props.pageTitle}
          description={props.pageDescription}
          walkthroughId={props.walkthroughId}
          dialog={dialog}
          setDialog={setDialog}
        />
        {props.children}
      </div>
    </div>
  );
};

const mapStateToProps = (state: AppState): AdminHtmlStateProps => ({
  mobileOpen: state.adminHtml.mobileOpen,
});

const mapDispatchToProps = (
  dispatch: Dispatch,
  ownProps: AdminHtmlOwnProps
): AdminHtmlDispatchProps => {
  return {
    onDrawerToggle: () => {
      dispatch(toggleMobileOpen());
    },
  };
};

export default connect<
  AdminHtmlStateProps,
  AdminHtmlDispatchProps,
  AdminHtmlOwnProps,
  AppState
>(
  mapStateToProps,
  mapDispatchToProps
)(AdminHtml);
